/**
 * Which foreground a tenant's colour can legally carry.
 *
 * The two mobile applications take tenant primary, secondary and accent
 * colours at runtime (Document 00; the brand-token contract in the design
 * commission). That is the feature, and it is also the hazard: a tenant picks a
 * colour for a logo, the application paints a button with it, and whoever chose
 * the label colour at design time never saw that pairing. A pale brand under
 * white text is not a slightly worse button — it is a button whose label
 * disappears in daylight, on a phone, for a driver who needs it.
 *
 * So the foreground is computed rather than chosen. This is the part of the
 * token system that is an algorithm rather than a taste decision, which is why
 * it can be written before the visual design exists and tested against the
 * cases a designer would be asked to prove.
 *
 * WCAG 2.2 relative luminance and contrast ratio, verbatim from the
 * specification rather than approximated: the sRGB channel transfer, the
 * 0.2126/0.7152/0.0722 weights, and (L1 + 0.05) / (L2 + 0.05).
 */
/** The ratios WCAG 2.2 requires, by the role the text plays. */
export const contrastRequirement = {
    /** 1.4.3 AA, text below 18.66px regular or 24px bold. */
    bodyText: 4.5,
    /** 1.4.3 AA, large text. */
    largeText: 3,
    /** 1.4.11 AA, icons and control boundaries. */
    nonText: 3,
    /** 1.4.6 AAA, body text. */
    bodyTextEnhanced: 7,
};
/**
 * Parse `#rgb`, `#rrggbb` or `#rrggbbaa`.
 *
 * Returns undefined rather than throwing or guessing. A tenant's branding is
 * configuration that arrives at runtime from a service, and a malformed value
 * must be a visible fallback to the platform palette, never an exception on a
 * render path or a silent black.
 */
export function parseHexColour(value) {
    const text = value.trim().replace(/^#/, "");
    if (!/^(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(text))
        return undefined;
    const expand = text.length === 3
        ? text
            .split("")
            .map((character) => character + character)
            .join("")
        : text.slice(0, 6);
    return {
        red: Number.parseInt(expand.slice(0, 2), 16),
        green: Number.parseInt(expand.slice(2, 4), 16),
        blue: Number.parseInt(expand.slice(4, 6), 16),
    };
}
/** WCAG 2.2 relative luminance. */
export function relativeLuminance(colour) {
    const channel = (value) => {
        const normalised = value / 255;
        return normalised <= 0.03928 ? normalised / 12.92 : ((normalised + 0.055) / 1.055) ** 2.4;
    };
    return (0.2126 * channel(colour.red) + 0.7152 * channel(colour.green) + 0.0722 * channel(colour.blue));
}
/** WCAG 2.2 contrast ratio, between 1 and 21. */
export function contrastRatio(first, second) {
    const a = relativeLuminance(first);
    const b = relativeLuminance(second);
    const lighter = Math.max(a, b);
    const darker = Math.min(a, b);
    return (lighter + 0.05) / (darker + 0.05);
}
/**
 * Pure black and white, and why they are not the default.
 *
 * Worth stating because it is provable and counter-intuitive. Contrast against
 * white is 1.05 / (L + 0.05) and against black is (L + 0.05) / 0.05, so the
 * better of the two is at its worst where they meet — at L = 0.1791, giving
 * 4.58:1. Sweeping the sRGB cube agrees to three decimals.
 *
 * The consequence: with pure black and white available, *no* colour can fail
 * WCAG AA for body text. A checker offering that pair and reporting a pass has
 * told you nothing, because it could not have reported anything else.
 *
 * Which means the guarantee belongs to the pair, not to the palette. The moment
 * a design system chooses a near-black ink — and they all do, because pure
 * black on a colour reads as a hole — it gives the guarantee up and failures
 * become real. So the candidates are part of the contract here, and the
 * defaults are the platform's own ink and paper rather than the pair that makes
 * the question unanswerable.
 */
export const PURE_BLACK_WHITE_FLOOR = 4.58;
/** The platform's text colours. Near-black on purpose; see above. */
export const platformInk = { ink: "#1f2933", paper: "#ffffff" };
/**
 * The most readable available foreground for a background, and whether it is
 * good enough.
 *
 * Two separate questions, and the second is the one that matters. A mid-tone
 * brand — the greys, muted teals and mid blues tenants keep choosing — can fail
 * 4.5:1 against every candidate a design system offers, and no choice of
 * foreground rescues it. Returning only a colour would hide that. A caller that
 * is told can keep the brand colour on fills and borders and take text back to
 * the platform palette, which is what a designer would decide anyway.
 */
export function readableForeground(background, options = {}) {
    const settings = typeof options === "number" ? { requirement: options } : options;
    const requirement = settings.requirement ?? contrastRequirement.bodyText;
    const candidates = settings.candidates ?? [platformInk.ink, platformInk.paper];
    let best;
    for (const candidate of candidates) {
        const parsed = parseHexColour(candidate);
        if (parsed === undefined)
            continue;
        const ratio = contrastRatio(background, parsed);
        if (best === undefined || ratio > best.ratio) {
            best = { foreground: candidate, ratio, meetsRequirement: ratio >= requirement };
        }
    }
    // Every candidate was malformed. Saying so is better than inventing a colour
    // that was never offered.
    return best ?? { foreground: platformInk.paper, ratio: 1, meetsRequirement: false };
}
/**
 * How close a tenant colour sits to a colour that means something.
 *
 * Danger is not decorative. If a tenant's primary is close enough to the
 * platform's danger red that an operator cannot tell a "cancel trip" button
 * from a branded one at a glance, the semantic colour has stopped being a
 * signal. This does not decide what to do about it — that is a product
 * decision, and the commission asks a designer to make it — it measures the
 * collision so the decision can be made from a number.
 *
 * Euclidean distance in sRGB is crude next to a perceptual space, and it is
 * deliberately the crude one: it needs no colour-science dependency, it runs on
 * a phone at render time, and it is used as a threshold for "look at this",
 * never as a claim about how two colours feel.
 */
export function colourDistance(first, second) {
    return Math.sqrt((first.red - second.red) ** 2 +
        (first.green - second.green) ** 2 +
        (first.blue - second.blue) ** 2);
}
//# sourceMappingURL=contrast.js.map